//LIBRARIES
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { browserHistory } from 'react-router';
import styled from 'styled-components';
import isEqual from 'lodash/isEqual';
import { MuiThemeProvider } from '@material-ui/core';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import StepContent from '@material-ui/core/StepContent';
import { List } from 'material-ui/List';
import FlatButton from '@material-ui/core/Button';
import { localize } from 'react-localize-redux';
import Auth from 'j-toker';

//HELPERS
import { muiThemeNext } from 'helpers/MUIFiles';
import { AccentColor } from 'helpers/AccentColorHelper';
import { featureEnabled } from 'helpers/ToggleBot';
import { hellosignClient } from 'helpers/hellosignClient';
import { apiGet } from 'helpers/ApiHelper';

//ACTIONS
import { getApplicationStatus, cancelLeadApplication } from 'actions/applicationStatusActions';

//CONSTANTS
import env from 'config/constants';

//STYLING
const StyledPendingSubmission = styled.p`
  font-weight: bold;
  margin: 0;
`;

const ApplicationInfo = connect(
  (store) => {
    return {
      document_id: store.applicationStatus.document_id,
      signer_id: store.applicationStatus.signer_id,
      stepIndex: store.applicationStatus.stepIndex,
      application_id: store.applicationStatus.application_id,
      delayUnitAssignment: store.applicationStatus.delay_unit_assignment,
      application_uuid: store.applicationStatus.application_uuid,
      hasApplication: store.dashboard.hasApplication,
      hasSigned: store.applicationStatus.has_signed,
      hasPaid: store.applicationStatus.has_paid,
      useHellosign: store.applicationStatus.use_hellosign,
      signUrl: store.applicationStatus.sign_url,
      hellosignApiAppId: store.applicationStatus.hellosign_api_app_id,
      applicants: store.dashboard.applicants,
      applicationMessage: store.applicationStatus.application_message,
    };
  },
  {
    getApplicationStatus,
    cancelLeadApplication,
  }
)(
  class ApplicationInfo extends Component {
    componentDidMount() {
      this.props.getApplicationStatus();
      hellosignClient.on('sign', () => {
        window.location.reload();
      });
    }

    redirectToSignPage = async () => {
      const custom_lease_creator_toggle = featureEnabled('custom_lease_creator');
      if (this.props.useHellosign && custom_lease_creator_toggle) {
        const { sign_url } = await apiGet(
          `${Auth.getApiUrl()}/signable_doc/sign_url?application_uuid=${
            this.props.application_uuid
          }`
        );

        hellosignClient.open(sign_url, {
          clientId: this.props.hellosignApiAppId,
          skipDomainVerification: env.ENV !== 'production',
        });
      } else if (this.props.document_id && this.props.signer_id) {
        browserHistory.push(
          `/internal_document_portal?document_uuid=${this.props.document_id}&document_type=lease&signer_uuid=${this.props.signer_id}`
        );
      }
    };

    cancelApplication = () => {
      this.props.cancelApplication(this.props.application_id);
    };

    render() {
      const { translate } = this.props;
      if (!this.props.hasApplication) return null;
      const hasApplicationMessage =
        this.props.applicationMessage &&
        this.props.applicationMessage.title &&
        this.props.applicationMessage.body;
      let colSpan = 'col-sm-12';
      if (hasApplicationMessage) {
        colSpan = 'col-sm-6';
      }
      return (
        <React.Fragment>
          {this.renderApplicationMessage(hasApplicationMessage)}
          <div className={colSpan}>
            <div className="grid-column">
              <h3 style={{ margin: '15px 0' }}>
                {translate('applicationStatus')}
                <b style={{ fontFamily: 'Circular-Black', color: AccentColor() }}>.</b>
              </h3>
              {this.renderApplicationStepper()}
            </div>
          </div>
        </React.Fragment>
      );
    }

    renderApplicationMessage = (shouldShow) => {
      if (!shouldShow) return null;

      const applicationMessage = this.props.applicationMessage;

      return (
        <div className="col-sm-6" style={{ padding: '0 30px 30px 15px' }}>
          <h3 style={{ margin: '15px 0' }}>{applicationMessage.title}</h3>
          <div dangerouslySetInnerHTML={{ __html: applicationMessage.body }} />
        </div>
      );
    };

    renderPendingApplicantSubmissions = () => {
      const { applicants } = this.props;
      let applicantType;

      return (
        <div>
          <span>{this.props.translate('applications.status.pendingApplicantSubmissions')}</span>
          {applicants.map((applicant) => {
            applicantType = applicant.is_guarantor ? 'Guarantor' : 'Applicant';
            if (isEqual(applicant.application_status, 'pending')) {
              return (
                <StyledPendingSubmission>{`${applicant.full_name}, ${applicantType}`}</StyledPendingSubmission>
              );
            }
            return null;
          })}
        </div>
      );
    };

    renderApplicationStepper = () => {
      const renderSteps = this.getSteps().map((label, index) => {
        return (
          <Step key={index}>
            <StepLabel>{label}</StepLabel>
            <StepContent>
              <p style={{ margin: 0 }}>{this.getStepContent(index)}</p>
              <div>{this.renderStepActions(this.props.stepIndex)}</div>
            </StepContent>
          </Step>
        );
      });

      return (
        <List style={{ padding: 0 }}>
          <div className="row">
            <MuiThemeProvider theme={muiThemeNext(AccentColor())}>
              <Stepper activeStep={this.props.stepIndex} orientation="vertical">
                {renderSteps}
              </Stepper>
            </MuiThemeProvider>
          </div>
        </List>
      );
    };

    renderStepActions = (step) => (
      <div style={{ marginRight: '12px 0' }}>
        {step === 2 && !this.props.hasSigned && (
          <FlatButton
            onClick={this.redirectToSignPage}
            style={{
              marginRight: '12px',
              backgroundColor: AccentColor(),
              color: 'white',
              borderRadius: '8px',
              zIndex: 1000,
            }}
          >
            Sign Lease
          </FlatButton>
        )}
      </div>
    );

    getSteps = () => {
      let steps = [
        this.props.translate('applications.status.pendingApplication'),
        this.props.translate('applications.status.pendingLandlordApproval'),
        this.props.translate('applications.status.pendingLeaseSignatures'),
        this.props.hasPaid
          ? this.props.translate('applications.messages.depositPaid')
          : this.props.translate('applications.messages.payDeposit'),
      ];

      if (this.props.delayUnitAssignment) {
        steps.push(this.props.translate('applications.status.pendingUnitAssignment'));
      }
      return steps;
    };

    getStepContent = (step) => {
      switch (step) {
        case 0:
          return this.renderPendingApplicantSubmissions();
        case 1:
          return this.props.translate('applications.messages.pendingLandlordApproval');
        case 2:
          return this.props.hasSigned
            ? this.props.translate('applications.messages.pendingAllLeadSignatures')
            : null;
        case 3:
          return this.props.hasPaid
            ? this.props.translate('applications.messages.depositPaid')
            : this.props.translate('applications.messages.payDeposit');
        default:
          return null;
      }
    };
  }
);

export default localize()(ApplicationInfo);
